import { dcbClient, triviaClient } from '../client';
import WorldplayService from '../worldplay/worldplayService';
import { brand } from 'brands';
import { setTriviaTokenFromResponse } from 'functions/triviaTokenStorage';
import { getDemoUser } from 'functions/demoAuth';
import { getFingerprintRequestId } from 'functions/fingerprint';
import { getCaptchaToken, TURNSTILE_WIDGET_ID } from 'functions/turnstile';
import type {
    DcbUserResponse,
    MsisdnResponse,
    RequestOtpResponse,
    SubscribeResponse,
    UnsubscribeResponse,
    ValidateOtpResponse,
} from '../../types/dcb';

const logAndThrow = (label: string, error: unknown): never => {
    console.error(`[${label} error]:`, error);
    throw error;
};

export default class DcbService {
    private static async captchaHeaders(action: string): Promise<Record<string, string>> {
        const token = await getCaptchaToken(action);
        return token
            ? {
                  'X-Captcha-Token': token,
                  'X-Captcha-Action': action,
                  'X-Captcha-Widget': TURNSTILE_WIDGET_ID,
              }
            : {};
    }

    /**
     * Where OTP is checked: DCB by serviceId, or for MTN, WorldPlay by the brand's
     * productId. Both go through trivia-v2, which issues the gameplay token.
     */
    private static otpTarget(serviceId: string): {
        path: string;
        product: { serviceId: string } | { productId: number };
    } {
        const { subscription } = brand;
        return subscription.provider === 'worldplay'
            ? { path: '/worldplay', product: { productId: subscription.productId } }
            : { path: '/dcb', product: { serviceId } };
    }

    static async requestOtp(
        msisdn: string | number,
        serviceId: string
    ): Promise<RequestOtpResponse | undefined> {
        try {
            const headers = await this.captchaHeaders('request_otp');
            const config = Object.keys(headers).length ? { headers } : undefined;
            const { path, product } = this.otpTarget(serviceId);

            const response = await triviaClient.post<RequestOtpResponse>(
                `${path}/send-otp`,
                {
                    msisdn: msisdn.toString(),
                    ...product,
                },
                config
            );
            setTriviaTokenFromResponse(response.data);
            return response.data;
        } catch (error) {
            logAndThrow('Request OTP', error);
        }
    }

    static async validateOtp(
        msisdn: string | number,
        serviceId: string,
        otp: string | number
    ): Promise<ValidateOtpResponse | undefined> {
        try {
            const fingerprintRequestId = await getFingerprintRequestId();
            const headers: Record<string, string> = {
                ...(fingerprintRequestId
                    ? { 'X-Fingerprint-Request-Id': fingerprintRequestId }
                    : {}),
                ...(await this.captchaHeaders('submit_otp')),
            };
            const config = Object.keys(headers).length ? { headers } : undefined;
            const { path, product } = this.otpTarget(serviceId);

            const response = await triviaClient.post<ValidateOtpResponse>(
                `${path}/validate-otp`,
                {
                    msisdn: msisdn.toString(),
                    ...product,
                    otp: otp.toString(),
                },
                config
            );
            setTriviaTokenFromResponse(response.data);
            return response.data;
        } catch (error) {
            logAndThrow('Validate OTP', error);
        }
    }

    static async encryptMsisdn(
        msisdn: string,
        config: { params?: Record<string, unknown> } = {}
    ): Promise<MsisdnResponse | undefined> {
        try {
            const response = await triviaClient.get<MsisdnResponse>('/encryption/encrypt-msisdn', {
                ...config,
                params: {
                    msisdn,
                    ...(config.params || {}),
                },
            });
            return response.data;
        } catch (error) {
            logAndThrow('Encrypt User Msisdn', error);
        }
    }

    static async decryptMsisdn(encryptedMsisdn: string): Promise<MsisdnResponse | undefined> {
        try {
            const fingerprintRequestId = await getFingerprintRequestId();
            const headers: Record<string, string> = {
                ...(fingerprintRequestId
                    ? { 'X-Fingerprint-Request-Id': fingerprintRequestId }
                    : {}),
                ...(await this.captchaHeaders('decrypt_msisdn')),
            };
            const config = Object.keys(headers).length ? { headers } : undefined;

            const response = await triviaClient.post<MsisdnResponse>(
                '/encryption/decrypt-msisdn',
                { encryptedMsisdn },
                config
            );
            setTriviaTokenFromResponse(response.data);
            return response.data;
        } catch (error) {
            logAndThrow('Decrypt Msisdn', error);
        }
    }

    static async getUserViaProductId(
        msisdn: string,
        productId: number
    ): Promise<DcbUserResponse | undefined> {
        // Demo mode: return the mock VIP/free user for whitelisted numbers instead
        // of the real lookup. Gated + inert in prod. Covers both initial login and
        // session restore, which both land here.
        const demoUser = getDemoUser(msisdn);
        if (demoUser) return demoUser;

        // MTN subscriptions live in WorldPlay, under the brand's own productId.
        // The productId passed in stays the trivia product used for gameplay.
        const { subscription } = brand;
        if (subscription.provider === 'worldplay') {
            return WorldplayService.getUser(msisdn, subscription.productId);
        }

        try {
            const response = await dcbClient.post<DcbUserResponse>('/get-user-via-product-id', {
                msisdn,
                productId,
            });
            return response.data;
        } catch (error) {
            logAndThrow('Get User Via Product ID', error);
        }
    }

    static async getUserViaSubscriptionId(
        subscriptionId: string,
        productId: number
    ): Promise<DcbUserResponse | undefined> {
        try {
            const fingerprintRequestId = await getFingerprintRequestId();
            const headers: Record<string, string> = {
                ...(fingerprintRequestId
                    ? { 'X-Fingerprint-Request-Id': fingerprintRequestId }
                    : {}),
                ...(await this.captchaHeaders('subscription_login')),
            };

            const response = await triviaClient.get<DcbUserResponse>(
                '/dcb/get-user-via-subscription-id',
                {
                    params: { subscriptionId, productId },
                    ...(Object.keys(headers).length ? { headers } : {}),
                }
            );
            return response.data;
        } catch (error) {
            logAndThrow('Get User Via Subscription ID', error);
        }
    }

    static async trackLogin(msisdn: string, service: string): Promise<unknown | undefined> {
        try {
            const response = await dcbClient.post('/track-login', { msisdn, service });
            return response.data;
        } catch (error) {
            logAndThrow('Track Login', error);
        }
    }

    static async createSessionId(
        msisdn: string,
        serviceId: string
    ): Promise<{ sessionId?: string } | undefined> {
        try {
            const response = await dcbClient.post<{ sessionId: string }>('/create-session-id', {
                msisdn,
                serviceId,
            });
            return response.data;
        } catch (error) {
            logAndThrow('Create Session ID', error);
        }
    }

    static async doiOne(msisdn: string, service: string): Promise<unknown | undefined> {
        try {
            const response = await dcbClient.post('/doi-one', { msisdn, serviceId: service });
            return response.data;
        } catch (error) {
            logAndThrow('DOI One', error);
        }
    }

    static async doiTwo(msisdn: string, service: string): Promise<unknown | undefined> {
        try {
            const response = await dcbClient.post('/doi-two', { msisdn, serviceId: service });
            return response.data;
        } catch (error) {
            logAndThrow('DOI Two', error);
        }
    }

    static async subscribeUser(
        msisdn: string,
        productId: number,
        serviceId: string,
        campaignId: string | number = 0,
        clickId?: string
    ): Promise<SubscribeResponse | undefined> {
        try {
            const response = await dcbClient.post<SubscribeResponse>('/subscribe-via-product-id', {
                msisdn,
                productId,
                serviceId,
                campaignId,
                clickId,
            });
            return response.data;
        } catch (error) {
            logAndThrow('Subscribe User', error);
        }
    }

    static async unSubscribeUser(
        msisdn: string,
        serviceId: string,
        isDcbOnly: boolean,
        sessionId?: string
    ): Promise<UnsubscribeResponse | undefined> {
        try {
            const response = await dcbClient.post<UnsubscribeResponse>('/unsubscribe', {
                msisdn,
                serviceId,
                sessionId,
                isDcbOnly,
            });
            return response.data;
        } catch (error) {
            logAndThrow('Unsubscribe User', error);
        }
    }

    static async createUsername(
        msisdn: string,
        serviceId: string,
        username: string
    ): Promise<unknown | undefined> {
        // Saved where getUserViaProductId reads it back from.
        const { subscription } = brand;
        if (subscription.provider === 'worldplay') {
            return WorldplayService.updateUsername(msisdn, subscription.productId, username);
        }

        try {
            const response = await dcbClient.post('/update-username-from-msisdn', {
                msisdn,
                serviceId,
                username,
            });
            return response.data;
        } catch (error) {
            logAndThrow('Create Username', error);
        }
    }

    static async addUserReferral(
        referrerMsisdn: string,
        refereeMsisdn: string,
        productId: number
    ): Promise<unknown | undefined> {
        try {
            const response = await dcbClient.post('/add-user-referral', {
                referrerMsisdn,
                refereeMsisdn,
                productId,
            });
            return response.data;
        } catch (error) {
            logAndThrow('Add User Referral', error);
        }
    }
}
